import { ref } from "vue";
import { useBezierDrawing } from "../utils/drawing/bezierDrawing";
import { CANVAS_SIZE } from "../constants/canvas";
import { hexToInt } from "../utils/color/colorUtils";
interface Position {
  x: number;
  y: number;
}

interface DrawingState {
  currentColor: string;
  brushSize: number;
  selectedTool: string;
  width: number;
}

export function useDrawingQueue(pixelSize: number, drawingState: DrawingState) {
  const rafId = ref<number | null>(null);
  const pointQueue = ref<Position[]>([]);
  const lastPoint = ref<Position | null>(null);

  const bezierDrawing = useBezierDrawing(drawingState.width, pixelSize);

  // Calculate optimal parameters based on canvas size
  const getOptimalParams = () => {
    const totalPixels = CANVAS_SIZE * CANVAS_SIZE;
    return {
      // Larger canvas = more points per frame
      maxPointsPerFrame: Math.min(Math.ceil(totalPixels / 1000), 200),
      // Interpolation distance increases with canvas size
      minInterpolationDistance: pixelSize * (CANVAS_SIZE > 256 ? 2.5 : 1.5),
      // Interpolation density decreases with canvas size
      interpolationDensity: pixelSize * (CANVAS_SIZE > 256 ? 1.0 : 0.5),
    };
  };

  const interpolatePoints = (start: Position, end: Position) => {
    const points: Position[] = [];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const { minInterpolationDistance, interpolationDensity } =
      getOptimalParams();

    if (distance < minInterpolationDistance) {
      return [end];
    }

    const baseSteps = Math.ceil(distance / interpolationDensity);
    const steps = Math.min(baseSteps, 100);

    for (let i = 0; i <= steps; i++) {
      points.push({
        x: start.x + (dx * i) / steps,
        y: start.y + (dy * i) / steps,
      });
    }
    return points;
  };

  const handleDrawingPoint = (
    x: number,
    y: number,
    currentPixels: Uint32Array
  ) => {
    const newPoint = { x, y };

    if (lastPoint.value) {
      const interpolatedPoints = interpolatePoints(lastPoint.value, newPoint);
      pointQueue.value.push(...interpolatedPoints);
    } else {
      pointQueue.value.push(newPoint);
    }

    lastPoint.value = newPoint;

    if (rafId.value === null) {
      const animate = () => {
        while (pointQueue.value.length > 0) {
          const point = pointQueue.value.shift();
          if (point) {
            bezierDrawing.addDrawingPoint(
              point.x,
              point.y,
              (indices) => {
                const color: string =
                  drawingState.selectedTool === "eraser"
                    ? ""
                    : drawingState.currentColor;
                indices.forEach((index) => {
                  if (index >= 0) {
                    currentPixels[index] = hexToInt(color);
                  }
                });
              },
              drawingState.brushSize
            );
          }
        }

        if (pointQueue.value.length > 0) {
          rafId.value = requestAnimationFrame(animate);
        } else {
          rafId.value = null;
        }
      };

      rafId.value = requestAnimationFrame(animate);
    }
  };

  const resetDrawingState = () => {
    if (rafId.value !== null) {
      cancelAnimationFrame(rafId.value);
      rafId.value = null;
    }
    lastPoint.value = null;
    pointQueue.value = [];
    bezierDrawing.resetDrawing();
  };

  return {
    handleDrawingPoint,
    resetDrawingState,
  };
}
